import React, { useContext, useRef, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  useBreakpointValue,
  Tooltip,
  Box,
  Flex,
  Text,
  Input,
} from "@chakra-ui/react";
import { FiFile } from "react-icons/fi";
import FileViewer from "../FileViewerModal/FileViewer";
import { AppContext } from "AppProvider/AppContext";
import { CheckIcon, DeleteIcon } from "@chakra-ui/icons";
import { appRequest } from "routes";
import CustomInput from "components/common/customInput/CustomInput";

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  selectedRows?: any;
  handleGet?: any;
};

function AddDocumentModal({
  isOpen,
  onClose,
  selectedRows,
  handleGet,
}: ModalProps) {
  const viewerWidth = useBreakpointValue({ lg: true });
  const { toastNotify, accountDetails, activeWorkflow, workFlowData } = useContext(AppContext);
  const [selectedFiles, setSelectedFiles] = useState<any[]>([]);
  const [selectFile, setSelectFile] = useState<any>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isHoveringDelete, setIsHoveringDelete] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [type, setType] = useState({label: "", value: ""});

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setSelectFile(null);
  };

  let width = "50vw";

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const newFiles = Array.from(files).map((file) => {
        const dt = URL.createObjectURL(file);
        return {
          name: file.name,
          url: dt,
          type: file.type,
          rawFile: file,
        };
      });
      setSelectedFiles((prev) => [...prev, ...newFiles]);
    } else {
      alert("Please upload a file");
    }
  };

  const onCloseUpload = () => {
    setSelectedFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onClose();
  };

  const handleDeleteFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadFileInChunks = async (file: File) => {
    const chunkSize = 1024 * 1024 * 2; // 2MB
    const totalChunks = Math.ceil(file.size / chunkSize);
    const uploadId = Date.now() + Math.random().toString(36).substr(2, 9);
    let finalFileId = null;

    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      const start = chunkIndex * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);

      const base64Chunk: string = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result as string;
          resolve(result.split(",")[1] || result);
        };
        reader.readAsDataURL(chunk);
      });

      const chunkResponse: any = await appRequest("document", "uploadChunk", {
        uploadId,
        chunkIndex,
        totalChunks,
        chunkData: base64Chunk,
        filename: file.name,
        contentType: file.type || "application/octet-stream"
      });

      if (chunkResponse?.status !== "success") {
        throw new Error("Failed to upload chunk " + chunkIndex);
      }
      if (chunkIndex === totalChunks - 1 && chunkResponse?.fileId) {
        finalFileId = chunkResponse.fileId;
      }
    }
    return finalFileId;
  };

  const handleSaveData = async () => {
    try {
      setLoading(true);

      const processedDocuments = [];
      for (const item of selectedFiles) {
        if (item.rawFile) {
          const fileId = await uploadFileInChunks(item.rawFile);
          processedDocuments.push({
            name: item.name,
            type: item.type,
            fileId: fileId,
          });
        } else {
          processedDocuments.push(item);
        }
      }

      const updatedStatus = {
        workflowId: activeWorkflow,
        documentId: selectedRows[0]?._id,
        additionalDocument: processedDocuments,
        level: accountDetails.level,
        type: type?.value
      };

      const response: any = await appRequest(
        "document",
        "uploadAddtionalDocument",
        updatedStatus
      );
      if (response.status !== "success") {
        throw new Error(response?.message || `Failed to update document for row`);
      }
      onClose();
      handleGet();
      toastNotify({
        type: "success",
        title: "Updates submitted",
        message: "Updated successfully.",
      });
      onClose();
    } catch (err) {
      console.error(err);
      toastNotify({
        title: "Error",
        message: "Failed to update document.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onCloseUpload} size="full" isCentered>
        <ModalOverlay />
        <ModalContent
          position="fixed"
          right={isOpen ? "0" : `-${width}`}
          top="0"
          height="100vh"
          width={viewerWidth ? width : "100vw"}
          animation={`${isOpen ? "slideIn" : "slideOut"
            } 0.8s ease-in-out forwards`}
        >
          <ModalHeader
            fontSize={{ base: "md", lg: "lg" }}
            color="teal.300"
            fontWeight="700"
            p=".6rem"
            ml="1rem"
            textTransform="capitalize"
          >
            Upload Documents
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {workFlowData.isAdditionalOptions && workFlowData.additionalDocOptions.length > 0 && (
              <CustomInput
                type="select"
                label="Select Type"
                name={`type`}
                placeholder="Select Option"
                options={workFlowData.additionalDocOptions}
                onChange={(e: any) =>
                  setType(e)
                }
                rest={{
                  width: { base: "10rem", lg: "100%" },
                }}

                isBoxSelect={true}
              />
            )}
            <Input
              name="documents"
              type="file"
              className="chooseFile"
              ref={fileInputRef}
              multiple
              accept={[".pdf", ".png", ".jpg", ".jpeg", ".webp", ".msg"].join(",")}
              onChange={(e: any) => handleFileChange(e)}
              mt={4}
            />

            {selectedFiles.length > 0 &&
              selectedFiles.map((file, index) => (
                <Box key={index}>
                  <Box
                    mt={5}
                    cursor="pointer"
                    bg="teal.100"
                    borderRadius={4}
                    p={2}
                    _hover={{ bg: "teal.200" }}
                    onClick={() => {
                      setIsDrawerOpen(true);
                      setSelectFile(file);
                    }}
                  >
                    <Tooltip
                      hasArrow
                      label="View Document"
                      arrowSize={15}
                      isDisabled={isHoveringDelete}
                    >
                      <Flex
                        alignItems="center"
                        justifyContent="space-between"
                        gap={2}
                      >
                        <Flex alignItems="center" gap={4}>
                          <Box color="teal.600">
                            <FiFile size={20} color="teal.500" />
                          </Box>
                          <Box
                            fontSize="lg"
                            p={{ base: 1, md: 2 }}
                            fontWeight="medium"
                            textDecoration="underline"
                            color="blue.500"
                          >
                            <Text
                              isTruncated
                              w={{ base: "12rem", md: "15rem" }}
                              fontSize="sm"
                            >
                              {file?.name}
                            </Text>
                          </Box>
                        </Flex>
                        <Tooltip label="Delete" hasArrow arrowSize={15}>
                          <Button
                            onMouseEnter={() => setIsHoveringDelete(true)}
                            onMouseLeave={() => setIsHoveringDelete(false)}
                            onClick={(e: any) => {
                              e.stopPropagation();
                              handleDeleteFile(index);
                            }}
                            cursor="pointer"
                            color="red.500"
                            bg="transparent"
                            _hover={{
                              color: "red.600",
                              borderColor: "red.600",
                            }}
                            borderColor="red.500"
                            fontSize="xs"
                          >
                            <DeleteIcon fontSize="md" />
                          </Button>
                        </Tooltip>
                      </Flex>
                    </Tooltip>
                  </Box>
                </Box>
              ))}
            <Modal
              isOpen={isDrawerOpen && selectFile}
              onClose={closeDrawer}
              size="full"
              isCentered
            >
              <ModalOverlay />
              <ModalContent
                position="fixed"
                right={isOpen ? "0" : `-${width}`}
                top="0"
                height="100vh"
                width={viewerWidth ? width : "100vw"}
                animation={`${isOpen ? "slideIn" : "slideOut"
                  } 0.8s ease-in-out forwards`}
              >
                <ModalHeader>
                  <Text fontSize="md" isTruncated w="15rem">
                    {selectFile?.name}
                  </Text>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody p={2}>
                  {selectFile && <FileViewer sample={selectFile} />}
                </ModalBody>
              </ModalContent>
            </Modal>
          </ModalBody>
          <ModalFooter>
            <Button
              isLoading={loading}
              onClick={() => handleSaveData()}
              colorScheme="blue"
              size="md"
              leftIcon={<CheckIcon />}
              _hover={{ bg: "blue.600", transform: "scale(1.05)" }}
              _active={{ bg: "blue.700", transform: "scale(1)" }}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
        <style>
          {`
          @keyframes slideIn {
            from {
              right: -${width};
            }
            to {
              right: 0;
            }
          }
          @keyframes slideOut {
            from {
              right: 0;
            }
            to {
              right: -${width};
            }
          }
          `}
        </style>
      </Modal>
    </>
  );
}

export default AddDocumentModal;